import React, { useEffect, useRef, useState } from 'react';
import { BorderBeam } from 'border-beam';
import { useTheme } from '@lognai/synapse-design-system-v2';
import type { AiThread } from '@lognai/plugin-ai-core';
import { useAiThreads } from '../../hooks/use-threads';
import { useEntityLabel } from '../../hooks/use-entity-label';

/**
 * What the search bar suggests you could ask, by the module you are standing in.
 *
 * It used to rotate one global list on every screen, so Freight Audit offered
 * "Show me all the latest RFQs" and "Which purchase orders are overdue?" — never
 * once something you might actually ask about the case in front of you
 * (QA ledger FA-007). A suggestion that is irrelevant everywhere teaches people
 * the box is decoration.
 *
 * Keyed by the first path segment, which is the module. Anything unlisted falls
 * back to the general set, so a new module is never worse off than before.
 */
const GENERAL_PLACEHOLDERS = [
  "What's the status of my shipment?",
  'Show me all the latest RFQs',
  'Which purchase orders are overdue?',
  'Summarise inventory below reorder level',
  'Show open sales orders this week',
];

const PLACEHOLDERS_BY_MODULE: Record<string, string[]> = {
  'freight-audit': [
    'Which invoices were overcharged this month?',
    'Summarise the open exceptions on this case',
    'Which vendor disputes the most exceptions?',
    'What is our total unrecovered variance?',
  ],
  shipments: [
    "What's the status of my shipment?",
    'Which shipments are arriving late?',
    'Show shipments still waiting to depart',
  ],
  orders: [
    'Which purchase orders are overdue?',
    'Show open sales orders this week',
    'Which orders are waiting on stock?',
  ],
  inventory: [
    'Summarise inventory below reorder level',
    'Which items are out of stock?',
    'What is reserved but not yet allocated?',
  ],
  'landed-cost': [
    'Which orders cost the most to land?',
    'What are we paying in customs and duty?',
    'Which landed costs are out of date?',
  ],
  invoices: [
    'Which invoices are overdue for payment?',
    'Show invoices awaiting approval',
    'What did we pay this vendor last quarter?',
  ],
  'freight-rfq': [
    'Show me all the latest RFQs',
    'Which RFQs are still awaiting quotes?',
    'Which carrier quoted lowest on this lane?',
  ],
  warehouse: [
    'Which locations are close to capacity?',
    'Show tasks still unassigned',
  ],
  products: ['Which products have no cost on file?', 'Show recently added products'],
};

const placeholdersFor = (moduleId?: string): string[] =>
  (moduleId && PLACEHOLDERS_BY_MODULE[moduleId]) || GENERAL_PLACEHOLDERS;

export const AskLognAIIcon = ({ size = 15, ...props }: React.SVGProps<SVGSVGElement> & { size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 20 20" fill="currentColor" {...props}>
    <path d="M13,3 L14.5,9.5 L21,11 L14.5,12.5 L13,19 L11.5,12.5 L5,11 L11.5,9.5 Z" />
    <path d="M5,1 L5.9,3.6 L8.5,4.5 L5.9,5.4 L5,8 L4.1,5.4 L1.5,4.5 L4.1,3.6 Z" />
  </svg>
);

const SendIcon = ({ active, size = 16 }: { active: boolean; size?: number }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none">
    {active && (
      <defs>
        <linearGradient id="lognai-send-icon-grad" x1="0%" y1="0%" x2="100%" y2="100%">
          <stop offset="0%" stopColor="var(--primary)" />
          <stop offset="100%" stopColor="var(--primary)" />
        </linearGradient>
      </defs>
    )}
    <path d="m3 3 3 9-3 9 19-9Z" fill={active ? 'url(#lognai-send-icon-grad)' : 'var(--border)'} />
    <path d="M6 12h16" stroke="white" strokeWidth="1.5" strokeLinecap="round" />
  </svg>
);

const formatRelative = (input: Date | string): string => {
  const d = typeof input === 'string' ? new Date(input) : input;
  const ms = Date.now() - d.getTime();
  const m = Math.floor(ms / 60_000);
  if (m < 1) return 'just now';
  if (m < 60) return `${m}m ago`;
  const h = Math.floor(m / 60);
  if (h < 24) return `${h}h ago`;
  const days = Math.floor(h / 24);
  return `${days}d ago`;
};

const css = `
  .lognai-search-wrapper {
    position: relative;
    display: inline-block;
    width: calc(32% - 16px);
  }
  .lognai-search-bar {
    background: var(--surface);
    border: 1px solid var(--border);
    transition: box-shadow 0.25s ease;
    cursor: text;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 36px;
    padding: 0 14px;
    border-radius: 6px;
    width: 100%;
    box-sizing: border-box;
  }
  .lognai-search-bar:hover {
    box-shadow: -2px 6px 12px color-mix(in srgb, var(--primary) 10%, transparent), 2px 6px 12px color-mix(in srgb, var(--primary) 10%, transparent);
  }
  .lognai-search-bar-icon {
    flex-shrink: 0;
  }
  .lognai-search-badge {
    flex-shrink: 0;
    display: inline-flex;
    align-items: center;
    gap: 4px;
    height: 22px;
    padding: 0 8px;
    border-radius: 4px;
    background: var(--primary);
    color: var(--primary-foreground, #fff);
    font-size: 10px;
    font-weight: 700;
    letter-spacing: 0.06em;
    white-space: nowrap;
    text-transform: uppercase;
  }
  .lognai-search-input-wrapper {
    flex: 1;
    position: relative;
    display: flex;
    align-items: center;
    overflow: hidden;
  }
  .lognai-search-input {
    width: 100%;
    background: transparent;
    border: none;
    outline: none;
    font-size: 13px;
    font-weight: 400;
    color: inherit;
    position: relative;
    z-index: 1;
  }
  .lognai-search-placeholder {
    position: absolute;
    left: 0;
    top: 50%;
    transform: translateY(-50%);
    font-size: 13px;
    font-weight: 400;
    color: var(--muted-foreground);
    pointer-events: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    width: 100%;
    z-index: 0;
  }
  .lognai-search-placeholder-fade {
    animation: lognaiPlaceholderFade 0.4s ease;
  }
  @keyframes lognaiPlaceholderFade {
    from { opacity: 0; transform: translateY(calc(-50% + 4px)); }
    to   { opacity: 1; transform: translateY(-50%); }
  }
  .lognai-send-btn {
    flex-shrink: 0;
    background: none;
    border: none;
    padding: 2px;
    display: flex;
    align-items: center;
    transition: opacity 0.2s ease;
  }
  .lognai-send-btn--active {
    cursor: pointer;
    opacity: 1;
  }
  .lognai-send-btn--disabled {
    cursor: default;
    opacity: 0.5;
    pointer-events: none;
  }
  .lognai-recent-dropdown {
    position: absolute;
    top: calc(100% + 6px);
    left: 0;
    right: 0;
    background: var(--surface);
    border: 1px solid color-mix(in srgb, var(--primary) 30%, transparent);
    border-radius: 8px;
    box-shadow: 0 4px 20px rgba(0,0,0,0.1), 0 1px 4px rgba(0,0,0,0.06);
    z-index: 200;
    overflow: hidden;
  }
  .lognai-recent-dropdown-header {
    padding: 10px 14px 4px;
    font-size: 11px;
    font-weight: 600;
    letter-spacing: 0.05em;
    color: var(--muted-foreground);
    text-transform: uppercase;
  }
  .lognai-recent-thread-row {
    display: flex;
    align-items: center;
    justify-content: space-between;
    gap: 8px;
    width: 100%;
    padding: 8px 14px;
    border: none;
    background: transparent;
    text-align: left;
    cursor: pointer;
    transition: background 0.15s;
  }
  .lognai-recent-thread-row:last-child {
    margin-bottom: 4px;
  }
  .lognai-recent-thread-row:hover {
    background: color-mix(in srgb, var(--primary) 7%, transparent);
  }
  .lognai-recent-thread-title {
    font-size: 13px;
    color: var(--foreground);
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
    flex: 1;
  }
  .lognai-recent-thread-time {
    font-size: 11px;
    color: var(--muted-foreground);
    flex-shrink: 0;
  }
`;

const humanize = (raw: string): string => {
  if (!raw) return raw;
  const spaced = raw.replace(/([a-z])([A-Z])/g, '$1 $2').toLowerCase();
  return spaced[0]!.toUpperCase() + spaced.slice(1);
};

interface RecentThreadRowProps {
  thread: AiThread;
  onClick: (id: string) => void;
}

const RecentThreadRow: React.FC<RecentThreadRowProps> = ({ thread, onClick }) => {
  const entityLabel = useEntityLabel({
    pluginId: thread.pinnedTo?.pluginId,
    entityType: thread.pinnedTo?.entityType,
    entityId: thread.pinnedTo?.entityId,
  });

  const title = (() => {
    if (thread.title && thread.title !== 'New conversation') return thread.title;
    if (thread.pinnedTo) {
      const typeLabel = humanize(thread.pinnedTo.entityType);
      if (entityLabel.label) return `${typeLabel} ${entityLabel.label}`;
      if (!entityLabel.isPending) return typeLabel;
    }
    return `Chat · ${formatRelative(thread.lastMessageAt ?? thread.updatedAt)}`;
  })();

  return (
    <button
      className="lognai-recent-thread-row"
      onClick={() => onClick(thread.id)}
    >
      <span className="lognai-recent-thread-title">{title}</span>
      <span className="lognai-recent-thread-time">{formatRelative(thread.lastMessageAt ?? thread.updatedAt)}</span>
    </button>
  );
};

export interface AskLognAISearchBarProps {
  onClick: (query?: string) => void;
  onOpenThread?: (threadId: string) => void;
  className?: string;
  /**
   * The module the user is in — the first path segment. Decides which example
   * questions rotate (QA ledger FA-007). Unknown or absent falls back to the
   * general set.
   */
  moduleId?: string;
}

export const AskLognAISearchBar: React.FC<AskLognAISearchBarProps> = ({
  onClick,
  onOpenThread,
  className,
  moduleId,
}) => {
  const placeholders = placeholdersFor(moduleId);
  const { theme } = useTheme();
  const [index, setIndex] = useState(0);
  const [animate, setAnimate] = useState(false);
  const [focused, setFocused] = useState(false);
  const [value, setValue] = useState('');
  const inputRef = useRef<HTMLInputElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);

  const threadsQuery = useAiThreads();
  const recentThreads = (threadsQuery.data ?? []).slice(0, 5);
  const showDropdown = focused && !value && recentThreads.length > 0;

  // Restart at the first suggestion when the module changes, so moving between
  // modules shows that module's questions rather than resuming mid-rotation.
  useEffect(() => { setIndex(0); }, [moduleId]);

  useEffect(() => {
    const id = setInterval(() => {
      setAnimate(false);
      requestAnimationFrame(() => {
        setIndex((i) => (i + 1) % placeholders.length);
        setAnimate(true);
      });
    }, 3000);
    return () => clearInterval(id);
  }, [placeholders.length]);

  const handleBarClick = () => {
    inputRef.current?.focus();
  };

  const handleBlur = (e: React.FocusEvent<HTMLDivElement>) => {
    if (!containerRef.current?.contains(e.relatedTarget as Node)) {
      setFocused(false);
    }
  };

  const handleSubmit = () => {
    const query = value.trim();
    onClick(query || undefined);
    setValue('');
    setFocused(false);
  };

  const handleThreadClick = (threadId: string) => {
    onOpenThread?.(threadId);
    setFocused(false);
  };

  const isActive = focused;

  return (
    <>
      <svg width="0" height="0" style={{ position: 'absolute' }}>
        <defs>
          <linearGradient id="lognai-search-grad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="var(--primary)" />
            <stop offset="100%" stopColor="var(--primary)" />
          </linearGradient>
        </defs>
      </svg>
      <style>{css}</style>
      <div
        ref={containerRef}
        className={`lognai-search-wrapper${className ? ` ${className}` : ''}`}
        onBlur={handleBlur}
      >
        <BorderBeam
          size="md"
          colorVariant="ocean"
          theme={theme}
          staticColors
          brightness={3}
          strength={1}
        >
          <div
            className="lognai-search-bar"
            onClick={handleBarClick}
          >
            <span className="lognai-search-badge">
              <AskLognAIIcon size={12} />
              Ask LognAI
            </span>
            <div className="lognai-search-input-wrapper">
              {!value && (
                <span className={`lognai-search-placeholder${animate ? ' lognai-search-placeholder-fade' : ''}`}>
                  {placeholders[index % placeholders.length]}
                </span>
              )}
              <input
                ref={inputRef}
                className="lognai-search-input"
                type="text"
                value={value}
                onChange={(e) => setValue(e.target.value)}
                onFocus={() => setFocused(true)}
                onKeyDown={(e) => { if (e.key === 'Enter') handleSubmit(); }}
                aria-label="Ask LognAI"
              />
            </div>
            <button
              className={`lognai-send-btn${isActive ? ' lognai-send-btn--active' : ' lognai-send-btn--disabled'}`}
              onClick={(e) => { e.stopPropagation(); handleSubmit(); }}
              tabIndex={isActive ? 0 : -1}
              aria-label="Send message"
              aria-disabled={!isActive}
            >
              <SendIcon active={isActive} size={16} />
            </button>
          </div>
        </BorderBeam>
        {showDropdown && (
          <div className="lognai-recent-dropdown">
            <div className="lognai-recent-dropdown-header">Recent chats</div>
            {recentThreads.map((t) => (
              <RecentThreadRow key={t.id} thread={t} onClick={handleThreadClick} />
            ))}
          </div>
        )}
      </div>
    </>
  );
};
